import { createMemoryRouter } from 'react-router-dom';

let routerHistory;

const createSpectateRouter = (rootElement) => {
    routerHistory = createMemoryRouter(
        [
            {
                path: '*',
                element: rootElement,
            },
        ],
        {
            initialEntries: [{
                state: {},
            }],
        },
    );

    return routerHistory;
};

/**
 * Pushes a new navigation context to the router history.
 *
 * CRITICAL: Uses requestAnimationFrame to defer navigation until after the current event completes.
 *
 * Problem encountered:
 * React Router's navigate() is synchronous and immediately triggers a full app re-render.
 * When called directly from a Link onClick handler, the Link component gets unmounted
 * mid-click, before React's synthetic event system finishes processing the click event.
 * This causes the onClick handler to never complete, breaking navigation.
 *
 * Solution:
 * By wrapping navigate() in requestAnimationFrame, we defer the navigation until the next
 * browser paint frame. This ensures the onClick event completes fully before navigation
 * occurs, allowing the Link to unmount safely after the event has finished.
 *
 * Without requestAnimationFrame:
 *      User clicks Link
 *          → onClick fires
 *              → navigateTo() called
 *                  → pushHistory() called
 *                      → routerHistory.navigate() IMMEDIATELY executes
 *                          → React re-renders entire app
 *                              → Link component UNMOUNTS mid-click ❌
 *                                  → Click event never completes
 *
 * With requestAnimationFrame:
 *      User clicks Link
 *          → onClick fires
 *              → navigateTo() called
 *                  → pushHistory() called
 *                      → requestAnimationFrame queues navigation for next paint frame
 *                          → onClick completes fully ✓
 *                              → THEN navigation happens
 *                                  → Link unmounts (but click already finished)
 *
 * @param {Object} context - The navigation context containing systemUrlPath and other route data
 * @returns {void}
 */
const pushHistory = (context) => {
    requestAnimationFrame(() => {
        routerHistory.navigate(
            context.systemUrlPath,
            {
                state: {
                    context: {...context},
                },
            }
        );
    });
};

const onRouterHistoryChange = (callback) => routerHistory.subscribe(callback);

/**
 * Same as useSpectateContext hook, try to use useSpectateContext instead
 * @returns {Object} the spectate context info
 */
const getSpectateContext = () => routerHistory?.state?.location?.state?.context || {};

export {
    onRouterHistoryChange,
    createSpectateRouter,
    pushHistory,
    getSpectateContext,
};
